import { ReactComponent as AppStore } from "assets/images/app-store.svg";
import { ReactComponent as GoogleStore } from "assets/images/google-play.svg";
import React, { useMemo } from "react";
import styled from "styled-components";
import FlexBox from "ui/FlexBox";
import Body from "ui/Text/Body";
import Heading from "ui/Text/Heading";
import VideoButton from "./VideoButton";
import { getStringWithLineBreak } from "utils/parse";
import { HeroSection } from "interfaces/content";

interface Props {
  className?: string;
  data: HeroSection["data"];
}

const AppInfo = ({ className, data }: Props) => {
  const heroInfo = data.info;

  const title = useMemo(() => getStringWithLineBreak(heroInfo?.title), [heroInfo?.title]);

  const subtitle = useMemo(
    () => getStringWithLineBreak(heroInfo?.subtitle),
    [heroInfo?.subtitle],
  );

  const storesInfo = useMemo(() => {
    if (!heroInfo?.stores) return null;

    const { title: storeTitle } = heroInfo.stores;

    return {
      title: getStringWithLineBreak(storeTitle),
    };
  }, [heroInfo?.stores]);

  return (
    <ContentContainer className={className}>
      {title && (
        <Title variant="h1" color="tertiary">
          {title}
        </Title>
      )}
      {subtitle && (
        <SubTitle variant="h2" color="tertiary">
          {subtitle}
        </SubTitle>
      )}
      {heroInfo?.videoUrl && (
        <VideoButtonContainer>
          <VideoButton src={heroInfo.videoUrl} />
        </VideoButtonContainer>
      )}
      {storesInfo && (
        <>
          {storesInfo.title && (
            <AppText variant="body2" color="tertiary">
              {storesInfo.title}
            </AppText>
          )}
          <AppButtonsContainer className="app-buttons-container">
            <a
              target="blank"
              aria-label="Google Play"
              href="https://play.google.com/store/apps/details?id=com.acin.iparque"
              rel="noopener"
            >
              <GoogleStore />
            </a>
            <a
              target="blank"
              aria-label="App Store"
              href="https://apps.apple.com/pt/app/iparque-mobile/id526743514"
              rel="noopener"
            >
              <AppStore />
            </a>
          </AppButtonsContainer>
        </>
      )}
    </ContentContainer>
  );
};

export default AppInfo;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: 16px;
`;

const AppButtonsContainer = styled(FlexBox)`
  gap: 20px;
`;

const Title = styled(Heading)`
  font-size: 2.8em;
  font-weight: ${({ theme }) => theme.typography.fontWeights.back};
  margin-bottom: 1.2em;
`;

const SubTitle = styled(Heading)`
  font-size: 2em;
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  margin-bottom: 1.2em;
`;

const AppText = styled(Body)`
  font-size: 2em;
  font-weight: ${({ theme }) => theme.typography.fontWeights.regular};
  margin-bottom: 1.2em;
`;

const VideoButtonContainer = styled.div`
  margin-bottom: 1.7em;
`;
